import { ApolloError, OperationVariables } from '@apollo/client'
import { DocumentNode } from 'graphql'
import * as React from 'react'
import VError from 'verror'

import { createContext } from '~logic/lib/react'
import { captureWithSentry, createExtras, createExtrasMap } from '~logic/sentry/captureWithSentry'

import { ApolloOperationError } from './helpers/ApolloOperationError'
import {
  getUserErrors,
  isNetworkError,
  isNetworkUnreachableError,
  isOperationError,
  shouldShowToUser,
} from './helpers/errorTypeHelpers'

type TOnOperationErrorOptions = {
  error: ApolloOperationError
  sentryEventId?: string
}

export type TApolloDefenderProps = RequiredChildren & {
  onUserErrors?(errors: ReadonlyArray<string>): void
  onNetworkError?(error: Error, isUnreachable: boolean): void
  onOperationError?(options: TOnOperationErrorOptions): void
  reportNetworkFailure?: boolean
}

export type TApolloDefenderContext = {
  /**
   * reports error to sentry & handles app state
   */
  onError: (error: Error) => void
  /**
   * handles app state
   */
  handleError: (error: Error) => void
  /**
   * reports error to sentry
   */
  reportError: (error: Error) => void
}

const [Provider, useApolloDefenderCtx] = createContext<TApolloDefenderContext>(`TApolloDefender`)

/**
 * Provider of the onError handler for apollo-related errors
 */
export const ApolloDefender: React.FC<TApolloDefenderProps> = ({
  onUserErrors,
  onNetworkError,
  onOperationError,
  reportNetworkFailure = false,
  children,
}) => {
  // report error to sentry
  const reportError = React.useCallback(
    (error: Error) => {
      if (isNetworkError(error)) {
        const isUnreachable = isNetworkUnreachableError(error)

        if (!isUnreachable && reportNetworkFailure) {
          captureWithSentry({
            error: new VError(error.networkError!, 'Network failure'),
            severityLevel: `debug`,
          })
        }

        return
      }

      if (isOperationError(error)) {
        const showToUserMessages = getUserErrors(error)

        // * set the scope information with which the error will be sent to Sentry
        const sentryScopeExtras = createExtrasMap()

        sentryScopeExtras.set('showToUser', showToUserMessages)
        sentryScopeExtras.set(
          'errors',
          error.graphQLErrors
            .filter((err) => !shouldShowToUser(err))
            .map((err) => ({
              message: err.message,
              path: err.path,
            })),
        )

        if (error.operationName) {
          sentryScopeExtras.set('operationName', error.operationName)
        }

        sentryScopeExtras.set('operationVariables', error.operationVariables)

        // send an operation error to Sentry
        captureWithSentry({
          error: new VError(error, `${error.operationType} ${error.operationName} failed`),
          tag: ['apolloError', `${error.operationType}Error`],
          extras: createExtras(sentryScopeExtras),
        })

        return
      }

      // send other errors (those that are not of type 'networkError', 'serviceUnreachable', 'userError' or 'operationError' ) to Sentry
      captureWithSentry({ error })
    },
    [reportNetworkFailure],
  )

  // handle the app state
  const handleError = React.useCallback(
    (error: Error) => {
      // * network error
      if (isNetworkError(error)) {
        const isUnreachable = isNetworkUnreachableError(error)

        onNetworkError?.(error.networkError, isUnreachable)
        return
      }

      // * operation error
      if (isOperationError(error)) {
        const showToUserMessages = getUserErrors(error)

        if (onUserErrors && showToUserMessages && showToUserMessages.length > 0) {
          onUserErrors(showToUserMessages)
          return
        }

        onOperationError?.({ error })
      }
    },
    [onNetworkError, onOperationError, onUserErrors],
  )

  // report error to sentry & handle the app state
  const onError = React.useCallback(
    (error: Error) => {
      reportError(error)
      handleError(error)
    },
    [handleError, reportError],
  )

  return <Provider value={{ onError, handleError, reportError }}>{children}</Provider>
}

export { useApolloDefenderCtx }

//* OPERATION ERROR HOOK
type TOperationErrorOptions = {
  error?: ApolloError
  document: DocumentNode
  variables?: OperationVariables
}

export const useApolloOperationDefender = () => {
  const { onError } = useApolloDefenderCtx()

  return {
    // operation error handler for use at the site where the operation is called
    onOperationError: React.useCallback(
      ({ error, document, variables }: TOperationErrorOptions) => {
        if (!error) {
          return null
        }

        return onError(new ApolloOperationError(error, document, variables))
      },
      [onError],
    ),
  }
}
